import { useMemo, useState, useCallback } from "react";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { useTheme } from "../../contexts/ThemeContext";
import { assetUrl } from "../../utils/galleryUtils";

const SIZE_PX = { large: 96, medium: 72, small: 48 };

function useAssociateBrandLogoSrc() {
  const { userInfo } = useContentSetting();
  const { publicPortalSetting } = useTheme();

  return useMemo(() => {
    const fromUser = userInfo?.portal_setting?.brand_logo;
    if (fromUser && String(fromUser).trim()) {
      const url = assetUrl(fromUser);
      if (url) return url;
    }
    const pub = publicPortalSetting?.brand_logo;
    if (pub && String(pub).trim()) {
      const url = assetUrl(pub);
      if (url) return url;
    }
    return null;
  }, [userInfo?.portal_setting?.brand_logo, publicPortalSetting?.brand_logo]);
}

function LoaderDots({ px }) {
  return (
    <div className="xino-brand-loader__dots" style={{ fontSize: px }} aria-hidden>
      <span className="xino-brand-loader__dot" />
      <span className="xino-brand-loader__dot" />
      <span className="xino-brand-loader__dot" />
    </div>
  );
}

function LoaderMark({ px, withOrbit }) {
  return (
    <div className="xino-brand-loader__frame" style={{ width: px, height: px }} aria-hidden>
      {withOrbit ? <div className="xino-brand-loader__orbit" /> : null}
      <div className="xino-brand-loader__x">
        <span className="xino-brand-loader__bar xino-brand-loader__bar--a" />
        <span className="xino-brand-loader__bar xino-brand-loader__bar--b" />
      </div>
    </div>
  );
}

function LoaderBrandMark({ px, withOrbit, logoSrc }) {
  const [imgFailed, setImgFailed] = useState(false);
  const onError = useCallback(() => setImgFailed(true), []);

  if (!logoSrc || imgFailed) {
    return <LoaderMark px={px} withOrbit={withOrbit} />;
  }

  return (
    <div className="xino-brand-loader__frame" style={{ width: px, height: px }} aria-hidden>
      {withOrbit ? <div className="xino-brand-loader__orbit" /> : null}
      <div className="xino-brand-loader__brand">
        <img
          src={logoSrc}
          alt=""
          className="xino-brand-loader__logo"
          decoding="async"
          onError={onError}
        />
      </div>
    </div>
  );
}

export default function AppLoader({
  button = false,
  pixelSize,
  color = "accent",
  message,
  size = "large",
  wrapperClassName,
  minHeight,
  className,
}) {
  const brandLogoSrc = useAssociateBrandLogoSrc();

  const colorMod =
    color === "onPrimary"
      ? "xino-brand-loader--on-primary"
      : color === "inherit"
        ? "xino-brand-loader--inherit"
        : "";

  let px;
  let useDots = false;

  if (button) {
    px = 22;
    useDots = true;
  } else if (pixelSize != null) {
    px = pixelSize;
    useDots = pixelSize <= 22;
  } else {
    px = SIZE_PX[size] ?? SIZE_PX.large;
  }

  const withOrbit = !useDots && px >= 44;
  const ariaLabel = message || "Loading";

  const rootClass = [
    "xino-brand-loader",
    useDots && !message ? "xino-brand-loader--dots-only" : "",
    colorMod,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const visual = useDots ? (
    <LoaderDots px={px} />
  ) : (
    <LoaderBrandMark key={brandLogoSrc ?? "mark"} px={px} withOrbit={withOrbit} logoSrc={brandLogoSrc} />
  );

  const fixedCore = (
    <div className={rootClass} role="status" aria-live="polite" aria-label={ariaLabel}>
      <span className="xino-brand-loader__sr-only">{ariaLabel}</span>
      {visual}
      {message ? <p className="xino-brand-loader__message">{message}</p> : null}
    </div>
  );

  const hostStyle = minHeight != null ? { minHeight } : undefined;
  const hostFill = minHeight != null || wrapperClassName;
  const host = (
    <div
      className={["xino-brand-loader-host", hostFill && "xino-brand-loader-host--fill"]
        .filter(Boolean)
        .join(" ")}
      style={hostStyle}
    >
      {fixedCore}
    </div>
  );

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{host}</div>;
  }

  return host;
}
